/**
 * Navigation helper functions for converting modal-based actions to page navigation
 */

/**
 * Generate navigation functions for CRUD operations
 * @param {string} basePath - Base path for the entity (e.g., 'products', 'categories')
 * @param {Function} navigate - React Router navigate function
 * @returns {Object} Object containing navigation functions
 */
export const createNavigationHelpers = (basePath, navigate) => {
  return {
    // Navigate to create new item page
    goToCreate: () => navigate(`/${basePath}/new`),
    
    // Navigate to edit item page
    goToEdit: (id) => navigate(`/${basePath}/${id}/edit`),
    
    // Navigate back to list page
    goToList: () => navigate(`/${basePath}`),
    
    // Navigate to item detail page (if needed)
    goToDetail: (id) => navigate(`/${basePath}/${id}`),
  };
};

/**
 * Common button configurations for consistent UI
 */
export const buttonConfigs = {
  primary: {
    className: "bg-primary-600 hover:bg-primary-700 text-white font-medium px-4 py-2 rounded-lg transition-colors duration-200 flex items-center gap-2",
  },
  secondary: {
    className: "bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium px-4 py-2 rounded-lg transition-colors duration-200 flex items-center gap-2",
  },
  success: {
    className: "bg-green-600 hover:bg-green-700 text-white font-medium px-4 py-2 rounded-lg transition-colors duration-200 flex items-center gap-2",
  },
  danger: {
    className: "bg-red-600 hover:bg-red-700 text-white font-medium px-4 py-2 rounded-lg transition-colors duration-200 flex items-center gap-2",
  },
  edit: {
    className: "text-indigo-600 hover:text-indigo-800 text-sm font-medium flex items-center gap-1",
  },
  delete: {
    className: "text-red-600 hover:text-red-800 text-sm font-medium flex items-center gap-1",
  },
};

/**
 * Common confirmation modal configurations
 */
export const confirmationConfigs = {
  delete: (itemName, itemType = 'item') => ({
    title: `Delete ${itemType}`,
    message: `Are you sure you want to delete ${itemName}? This action cannot be undone.`,
    confirmText: "Delete",
    cancelText: "Cancel",
    type: "danger"
  }),
  
  archive: (itemName, itemType = 'item') => ({
    title: `Archive ${itemType}`,
    message: `Are you sure you want to archive ${itemName}? It will no longer be visible in the main list.`,
    confirmText: "Archive",
    cancelText: "Cancel",
    type: "warning"
  }),
  
  restore: (itemName, itemType = 'item') => ({
    title: `Restore ${itemType}`,
    message: `Are you sure you want to restore ${itemName}? It will be visible in the main list again.`,
    confirmText: "Restore",
    cancelText: "Cancel",
    type: "info"
  }),
};

/**
 * Generate standard page header configuration
 */
export const createPageHeader = (title, description, actions = []) => ({
  title,
  description,
  actions,
});

/**
 * Generate standard empty state configuration
 */
export const createEmptyState = (title, description, actionText, actionHandler, icon = null) => ({
  title,
  description,
  actionText,
  actionHandler,
  icon,
});

/**
 * Utility function to handle API errors consistently
 */
export const handleApiError = (error, defaultMessage = 'An error occurred') => {
  return error.response?.data?.message || error.message || defaultMessage;
};

/**
 * Utility function to format dates consistently
 */
export const formatDate = (date, options = {}) => {
  if (!date) return '';
  
  const defaultOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...options
  };
  
  return new Date(date).toLocaleDateString(undefined, defaultOptions);
};

/**
 * Utility function to truncate text with ellipsis
 */
export const truncateText = (text, maxLength = 100) => {
  if (!text || text.length <= maxLength) return text;
  return text.substring(0, maxLength).trim() + '...';
};

/**
 * Utility function to generate status badge classes
 */
export const getStatusBadgeClass = (status, isActive = true) => {
  const baseClasses = 'px-2 py-1 text-xs rounded-full font-medium';
  
  if (typeof status === 'boolean') {
    return isActive 
      ? `${baseClasses} bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200`
      : `${baseClasses} bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200`;
  }
  
  const statusClasses = {
    active: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    inactive: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
    pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
    success: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    error: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
    warning: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
    info: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  };
  
  return `${baseClasses} ${statusClasses[status] || statusClasses.info}`;
};